import { DidUser, useAuth } from '@/services/auth';
import {
    ImageType,
    isDvpPresenter,
    isExpressivePresenter,
    isExpressPresenter,
    isPremiumPresenter,
    Presenter,
    VideoType,
} from '@d-id/types';

export const isUserAvatar = (avatar: Presenter, userSub?: string) => {
    return (
        avatar.presenterType === ImageType.Dreams ||
        (avatar.presenterType === ImageType.Upload && !avatar.isDefault) ||
        (isExpressPresenter(avatar) && avatar.created_by === userSub) ||
        (isDvpPresenter(avatar) && avatar.owner_id === userSub)
    );
};

export const isUserClipAvatar = (avatar: Presenter) => {
    return !!avatar.owner_id && avatar.owner_id !== 'PUBLIC_D-ID';
};

export const isGreenScreenAvatar = (avatar: Presenter) => {
    return avatar.is_green_screen;
};

export enum AvatarFilter {
    All = 'all',
    MyAvatars = 'my_avatars',
    VideoAvatars = 'video_avatars',
    PhotoAvatars = 'photo_avatars',
}

export enum AvatarFilterDeprecated {
    All = 'all',
    MyAvatars = 'my_avatars',
    Premium = 'premium',
    PremiumPlus = 'premium_plus',
    Standard = 'standard',
    Express = 'express',
}

export const isStandardAvatar = (avatar: Presenter) => {
    return !isUserAvatar(avatar) && avatar.type === VideoType.TALK;
};

const isDreamsAvatar = (avatar: Presenter) => {
    return avatar.presenterType === ImageType.Dreams;
};

const isUploadAvatar = (avatar: Presenter) => {
    return avatar.presenterType === ImageType.Upload && !isVideoAvatar(avatar);
};

const isVideoAvatar = (avatar: Presenter) => {
    return isPremiumPresenter(avatar) || isExpressPresenter(avatar) || isExpressivePresenter(avatar);
};

const isPhotoAvatar = (avatar: Presenter) => {
    return isStandardAvatar(avatar) || isDreamsAvatar(avatar) || isUploadAvatar(avatar);
};

export const avatarToFilter = (avatar?: Presenter, user?: DidUser | null) => {
    if (!avatar) return;

    if (isUserAvatar(avatar, user?.sub)) {
        return AvatarFilter.MyAvatars;
    }

    if (isPhotoAvatar(avatar)) {
        return AvatarFilter.PhotoAvatars;
    }

    if (isVideoAvatar(avatar)) {
        return AvatarFilter.VideoAvatars;
    }

    // deprecated filters
    if (isPremiumPresenter(avatar) && !isDvpPresenter(avatar)) {
        return AvatarFilterDeprecated.Premium;
    }
    if (isDvpPresenter(avatar)) {
        return AvatarFilterDeprecated.PremiumPlus;
    }
    if (isStandardAvatar(avatar)) {
        return AvatarFilterDeprecated.Standard;
    }
    if (isExpressPresenter(avatar)) {
        return AvatarFilterDeprecated.Express;
    }
    // deprecated filters end

    return AvatarFilter.All;
};

export const useAvatarsFilters = (
    avatars: Presenter[] = [],
    filter: AvatarFilter | AvatarFilterDeprecated,
    search?: string
): Presenter[] => {
    const { user } = useAuth();
    const initiallyFilteredAvatars = avatars
        .filter(a => !a.id?.includes('-removed_bg'))
        .filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()));

    // uploads and dreams
    if (filter === AvatarFilter.MyAvatars) {
        return initiallyFilteredAvatars.filter(a => isUserAvatar(a, user?.sub));
    }

    if (filter === AvatarFilter.VideoAvatars) {
        return initiallyFilteredAvatars.filter(a => isVideoAvatar(a));
    }

    if (filter === AvatarFilter.PhotoAvatars) {
        return initiallyFilteredAvatars?.filter(a => isPhotoAvatar(a));
    }

    // deprecated filters
    if (filter === AvatarFilterDeprecated.Premium) {
        return initiallyFilteredAvatars.filter(a => isPremiumPresenter(a) && !isDvpPresenter(a));
    }

    if (filter === AvatarFilterDeprecated.PremiumPlus) {
        return initiallyFilteredAvatars.filter(a => isDvpPresenter(a));
    }

    if (filter === AvatarFilterDeprecated.Standard) {
        return initiallyFilteredAvatars?.filter(isStandardAvatar);
    }

    if (filter === AvatarFilterDeprecated.Express) {
        return initiallyFilteredAvatars.filter(isExpressPresenter);
    }
    // deprecated filters end

    return initiallyFilteredAvatars;
};
